/**
 * Helper functions to control field editability based on amend type
 */

export type AmendType = 'Material' | 'Non-Material' | 'Both' | null;

/**
 * Sections that are considered "Material Fields" for ACA amend
 * These are editable when amendType is 'Material'
 * This includes the "Application Details" form which contains all the detailed application sections
 */
const MATERIAL_FIELD_SECTIONS = [
    // All sections within the "application-details-amend" form
    'company-details-amend',
    'ownership-amend',
    'financial-details-amend',
    'funding-credit-information-amend',
    'business-plans-amend',
    'personnel-amend',
    'information-insolvency-amend',
    'file-attachments-amend',
];

/**
 * Sections that are considered "Non-Material Fields" for ACA amend
 * These are editable when amendType is 'Non-Material'
 * This includes "Applicant/Filer Details" and "Billing Details" forms
 */
const NON_MATERIAL_FIELD_SECTIONS = [
    // Sections within the "general-amend" form (Applicant/Filer Details)
    'applicant-type-section',
    'filer-details',
    'applicant-details',
    // Sections within the "contact-billing-details-amend" form (Billing Details)
    'contact-particulars',
    'billing-details',
];

/**
 * Determine if a field should be disabled based on amend type and section
 * @param sectionId - The section ID the field belongs to
 * @param amendType - The current amend type ('Material', 'Non-Material', or 'Both')
 * @param isAmendMode - Whether the application is in amend mode
 * @returns true if the field should be disabled, false otherwise
 */
export const isFieldDisabledInAmend = (
    sectionId: string,
    amendType: AmendType,
    isAmendMode: boolean
): boolean => {
    // If not in amend mode, don't disable anything
    if (!isAmendMode || !amendType) {
        return false;
    }

    // If 'Both' is selected, allow editing all fields
    if (amendType === 'Both') {
        return false;
    }

    const isMaterialSection = MATERIAL_FIELD_SECTIONS.some(section => sectionId.includes(section));
    const isNonMaterialSection = NON_MATERIAL_FIELD_SECTIONS.some(section => sectionId.includes(section));

    // If Material amend type is selected
    if (amendType === 'Material') {
        // Disable non-material sections, allow material sections
        return isNonMaterialSection;
    }

    // If Non-Material amend type is selected
    if (amendType === 'Non-Material') {
        // Disable material sections, allow non-material sections
        return isMaterialSection;
    }

    return false;
};

/**
 * Determine if an entire section should be disabled
 * @param sectionId - The section ID
 * @param amendType - The current amend type
 * @param isAmendMode - Whether in amend mode
 * @returns true if the section should be disabled
 */
export const isSectionDisabledInAmend = (
    sectionId: string,
    amendType: AmendType,
    isAmendMode: boolean
): boolean => {
    return isFieldDisabledInAmend(sectionId, amendType, isAmendMode);
};

/**
 * Get a helper message to show users why fields are disabled
 * @param amendType - The current amend type
 * @returns A message explaining which fields can be edited
 */
export const getAmendModeMessage = (amendType: AmendType): string => {
    if (amendType === 'Material') {
        return 'You can only edit Material Fields (Application Details). Contact and Billing details are locked.';
    }
    if (amendType === 'Non-Material') {
        return 'You can only edit Non-Material Fields (Contact and Billing Details). Application details are locked.';
    }
    if (amendType === 'Both') {
        return 'You can edit all fields (Material and Non-Material).';
    }
    return '';
};
